import React from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { URL } from '@/config/constants';

const FOOTER_LINKS_LEFT = [
  { label: 'ショッピングガイド', href: URL.ec.products.top },
  {
    label: 'FAQ/お客さまサポート',
    href: 'https://medulla.co.jp/customer_support',
  },
] as const;

const FOOTER_LINKS_RIGHT = [
  { label: '特商法取引法に基づく表記', href: URL.ec.tokusho },
  { label: 'セキュリティポリシー', href: URL.securityPolicy },
  { label: 'プライバシーポリシー', href: URL.privacyPolicy },
  { label: '利用規約', href: URL.terms },
  { label: 'よくあるご質問', href: URL.faq },
  { label: '会社情報', href: URL.company },
] as const;

const footerLinkClass =
  'text-[#DEDEDC] text-left md:text-center text-xs font-normal leading-[150%] tracking-[0.96px] hover:underline';

const footerTextStyle: React.CSSProperties = {
  fontFamily: '"Source Han Sans JP", sans-serif',
};

export function ClubFooter() {
  return (
    <footer className="bg-[#757574] text-white">
      <div className="w-full mx-auto max-w-xl md:max-w-[1280px] pt-8 md:pt-[3.5rem] px-6 md:px-8 grid grid-cols-[1fr_auto] grid-rows-[auto_auto_auto] gap-y-6 md:grid-cols-none md:grid-rows-none md:block md:gap-0">
        {/* トップ: ロゴ + SNSアイコン（SP: Gridでロゴ1行・SNSは下段右に配置） */}
        <div className="contents md:flex flex-col md:flex-row md:items-center md:justify-between md:gap-6 pb-0 md:pb-[3.25rem]">
          <Link
            href={URL.officialWebsite}
            className="flex-shrink-0 row-start-1 col-span-full md:row-auto md:col-auto"
            aria-label="MEDULLA Brand Site"
          >
            <Image
              src="/images/common/logo-05.svg"
              width={300}
              height={38}
              alt="MEDULLA"
              className="w-[200px] md:w-[300px] max-w-full h-auto object-contain"
            />
          </Link>
          <div className="flex items-center gap-4 row-start-3 col-start-2 justify-self-end self-end md:row-auto md:col-auto md:ml-auto md:justify-self-auto md:self-auto">
            <a
              href={URL.sns.instagram}
              target="_blank"
              rel="noopener noreferrer"
              className="text-white hover:opacity-80 transition-opacity"
              aria-label="Instagram"
            >
              <Image
                src="/icon/club-icon/instagram.svg"
                width={24}
                height={24}
                alt=""
                className="w-6 h-6"
              />
            </a>
            <a
              href={URL.sns.line}
              target="_blank"
              rel="noopener noreferrer"
              className="text-white hover:opacity-80 transition-opacity"
              aria-label="LINE"
            >
              <Image
                src="/icon/club-icon/line.svg"
                width={27}
                height={26}
                alt=""
                className="w-6 h-6 object-contain"
              />
            </a>
            <a
              href={URL.sns.x}
              target="_blank"
              rel="noopener noreferrer"
              className="text-white hover:opacity-80 transition-opacity"
              aria-label="X"
            >
              <Image
                src="/icon/club-icon/x.svg"
                width={24}
                height={24}
                alt=""
                className="w-6 h-6"
              />
            </a>
          </div>
        </div>

        {/* ミドル: ナビ・ポリシーリンク（SP: 2リンク縦→6リンク左、PC: 従来どおり） */}
        <div className="contents md:flex flex-col md:flex-row md:items-center md:justify-between md:gap-4 pt-0 md:pt-[3.25rem] pb-0 md:pb-6">
          <div
            className="flex flex-col gap-y-2 row-start-2 col-span-full md:row-auto md:col-auto md:flex-row md:flex-wrap md:items-center md:gap-x-2 md:gap-y-1"
            style={footerTextStyle}
          >
            <Link href={FOOTER_LINKS_LEFT[0].href} className={footerLinkClass}>
              {FOOTER_LINKS_LEFT[0].label}
            </Link>
            <span
              className="hidden md:inline text-[#DEDEDC] text-xs"
              aria-hidden
            >
              |
            </span>
            <Link
              href={FOOTER_LINKS_LEFT[1].href}
              className={footerLinkClass}
              target="_blank"
              rel="noopener noreferrer"
            >
              {FOOTER_LINKS_LEFT[1].label}
            </Link>
          </div>
          <nav
            className="flex flex-col gap-y-2 row-start-3 col-start-1 md:row-auto md:col-auto md:flex-row md:flex-wrap md:items-center md:justify-end md:gap-x-3 md:gap-y-1"
            style={footerTextStyle}
            aria-label="ポリシー・会社情報"
          >
            {FOOTER_LINKS_RIGHT.map((item) => (
              <Link
                key={item.label}
                href={item.href}
                className={footerLinkClass}
                target={item.href.startsWith('http') ? '_blank' : undefined}
                rel={
                  item.href.startsWith('http')
                    ? 'noopener noreferrer'
                    : undefined
                }
              >
                {item.label}
              </Link>
            ))}
          </nav>
        </div>
      </div>

      {/* ボトム: 著作権（背景は画面幅いっぱい、中身は max-width で揃える） */}
      <div className="mt-8 md:mt-5 bg-[#60605F]/40 py-3 md:py-4">
        <div className="w-full mx-auto max-w-xl md:max-w-[1280px] px-6 md:px-8">
          <p className="text-xs text-white/90">© Sparty, Inc.</p>
        </div>
      </div>
    </footer>
  );
}

export default ClubFooter;
